// src/pages/ApiKeys.js — MIDTC API (2026-09-14)
//
// Assistant API keys, laid out like the Software catalog: header + search on
// top, a card grid, a detail panel on the right, a modal to create.
//
// WHY THIS EXISTS: the browser extension's options screen told people to go to
// "Settings → Access API → Create a key". That screen had never been built —
// the endpoints (/api/assistant/keys) worked, but nothing in the app called
// them, so there was no way to get a key without someone minting one by hand.
//
// ⚠️ THE PLAINTEXT KEY IS RETURNED EXACTLY ONCE, by the POST that creates it.
// The server stores only a SHA-256 hash, so it cannot be shown again later and
// there is no "reveal" to build. That is why `created` is held in state and
// rendered as a banner that has to be dismissed deliberately.
import { useState, useEffect, useCallback } from "react";

const API = "https://ticketing.midtc.com/api";
const tok = () => localStorage.getItem("midtc_token") || "";
const apiFetch = (path, opts = {}) =>
  fetch(API + path, { headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" }, ...opts });

// Mirrors SCOPES / OUTBOUND_SCOPES in backend/src/assistantApi.js. `read` is
// implied by every other scope — the server adds it back if you leave it off.
const SCOPE_INFO = {
  "read":            { label: "read",            color: "#4f6fff", blurb: "Look things up — tickets, schedule, spell check. Implied by every other scope." },
  "schedule":        { label: "schedule",        color: "#10b981", blurb: "Start and stop timers, schedule a ticket, create a task." },
  "reply-to-ticket": { label: "reply-to-ticket", color: "#f59e0b", blurb: "Post a reply on a ticket. Reaches the customer.", outbound: true },
  "send-email":      { label: "send-email",      color: "#ef4444", blurb: "Send an email as you. Reaches the customer.", outbound: true },
};
const OUTBOUND = new Set(["reply-to-ticket", "send-email"]);

const fmtD = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const fmtDT = (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "never";
const daysLeft = (d) => d == null ? null : Math.ceil((new Date(d) - Date.now()) / 86400000);

export default function ApiKeys() {
  const [keys, setKeys]       = useState([]);
  const [scopes, setScopes]   = useState(Object.keys(SCOPE_INFO));
  const [loading, setLoading] = useState(true);
  const [detail, setDetail]   = useState(null);
  const [editing, setEditing] = useState(null);   // {} while the create modal is open
  const [created, setCreated] = useState(null);   // the one-time plaintext key
  const [search, setSearch]   = useState("");
  const [copied, setCopied]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch("/assistant/keys");
      const d = await r.json();
      setKeys(Array.isArray(d.keys) ? d.keys : []);
      if (Array.isArray(d.available_scopes) && d.available_scopes.length) setScopes(d.available_scopes);
    } catch { setKeys([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this key? Anything using it stops working immediately. This cannot be undone.")) return;
    await apiFetch(`/assistant/keys/${id}`, { method: "DELETE" });
    if (detail?.id === id) setDetail(null);
    load();
  };

  const copyKey = async () => {
    try { await navigator.clipboard.writeText(created.key); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard blocked — the key is on screen to select by hand */ }
  };

  const filtered = keys.filter(k => !search ||
    k.label.toLowerCase().includes(search.toLowerCase()) ||
    (k.scopes || []).some(s => s.toLowerCase().includes(search.toLowerCase())));

  const panel = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 10 };
  const live = keys.filter(k => !k.expires_at || new Date(k.expires_at) > Date.now()).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden", background: "var(--t-page)" }}>
      {/* Header */}
      <div style={{ padding: "20px 24px 14px", borderBottom: "1px solid var(--t-border-soft)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--t-text-primary)", letterSpacing: "-0.5px" }}>MIDTC API</h1>
            <p style={{ margin: "3px 0 0", color: "var(--t-text-faint)", fontSize: 13 }}>
              {live} active key{live === 1 ? "" : "s"} · for the browser extension, scripts and anything else that talks to the assistant
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search keys…"
              style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "7px 12px", color: "var(--t-text-body)", fontSize: 12.5, width: 180 }} />
            <button onClick={() => setEditing({ label: "", scopes: ["read"], expires_days: 365, require_confirm: true })}
              style={{ background: "linear-gradient(135deg,#4f6fff,#7b61ff)", color: "#fff", border: "none", borderRadius: 7, padding: "7px 16px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              + Create a key
            </button>
          </div>
        </div>

        <div style={{ marginTop: 12, background: "rgba(79,111,255,0.07)", border: "1px solid rgba(79,111,255,0.25)", borderRadius: 8, padding: "9px 13px", fontSize: 12, color: "#8fa6ff", lineHeight: 1.6 }}>
          Keys are personal — these are yours alone and act as you. For the <b>MIDTC Assistant</b> browser
          extension, create one with <code>read</code> and <code>schedule</code>, then paste it into the
          extension&rsquo;s options page. Only a hash is stored here, so a key is shown once and never again.
        </div>
      </div>

      {/* The one-time key. Deliberately loud and deliberately manual to dismiss. */}
      {created && (
        <div style={{ margin: "16px 24px 0", background: "rgba(16,185,129,0.09)", border: "1px solid rgba(16,185,129,0.4)", borderRadius: 10, padding: 16, flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#34d399", marginBottom: 4 }}>
            “{created.label}” created — copy it now
          </div>
          <div style={{ fontSize: 12, color: "var(--t-text-faint)", marginBottom: 10 }}>
            This is the only time the key is shown. The server keeps just a hash of it; if you lose it, revoke it and make another.
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <code style={{ flex: 1, minWidth: 260, background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 7,
                           padding: "9px 12px", fontSize: 12.5, color: "var(--t-text-primary)", fontFamily: "monospace", wordBreak: "break-all", userSelect: "all" }}>
              {created.key}
            </code>
            <button onClick={copyKey}
              style={{ background: copied ? "#10b981" : "linear-gradient(135deg,#4f6fff,#7b61ff)", color: "#fff", border: "none", borderRadius: 7, padding: "9px 16px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
              {copied ? "Copied ✓" : "Copy"}
            </button>
            <button onClick={() => setCreated(null)}
              style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", color: "#94a3b8", borderRadius: 7, padding: "9px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              I&rsquo;ve saved it
            </button>
          </div>
        </div>
      )}

      <div style={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden" }}>
        {/* Key list */}
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          {loading && <div style={{ textAlign: "center", padding: 60, color: "#475569" }}>Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: 60, color: "#475569" }}>
              <div style={{ fontSize: 34, marginBottom: 10 }}>🔑</div>
              {keys.length ? "No keys match that search." : "No keys yet — create one for the browser extension."}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {filtered.map(k => {
              const sel = detail?.id === k.id;
              const left = daysLeft(k.expires_at);
              const expired = left != null && left <= 0;
              return (
                <div key={k.id} onClick={() => setDetail(d => d?.id === k.id ? null : k)}
                  style={{ ...panel, borderColor: sel ? "#4f6fff" : "var(--t-border-soft)", padding: 14, cursor: "pointer", transition: "border-color 0.15s", opacity: expired ? 0.55 : 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--t-text-primary)" }}>{k.label}</div>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: expired ? "#64748b" : "#34d399", flexShrink: 0, marginTop: 4 }}
                          title={expired ? "expired" : "active"} />
                  </div>
                  <div style={{ fontSize: 11.5, color: "#94a3b8", fontFamily: "monospace", margin: "6px 0 8px" }}>
                    mdta_{k.key_prefix}…
                  </div>
                  <div style={{ display: "flex", gap: 5, marginBottom: 8, flexWrap: "wrap" }}>
                    {(k.scopes || []).map(s => {
                      const si = SCOPE_INFO[s] || { label: s, color: "#64748b" };
                      return (
                        <span key={s} style={{ background: si.color + "22", color: si.color, border: `1px solid ${si.color}44`, borderRadius: 99, padding: "1px 9px", fontSize: 10.5, fontWeight: 700 }}>
                          {si.label}
                        </span>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>
                    {k.call_count || 0} call{k.call_count === 1 ? "" : "s"} · last used {fmtDT(k.last_used_at)}
                  </div>
                  <div style={{ fontSize: 10.5, color: expired ? "#f87171" : "#475569", marginTop: 3 }}>
                    {k.expires_at ? (expired ? `expired ${fmtD(k.expires_at)}` : `expires ${fmtD(k.expires_at)} · ${left}d left`) : "never expires"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail panel */}
        {detail && (
          <div style={{ width: 400, flexShrink: 0, borderLeft: "1px solid var(--t-border-soft)", background: "var(--t-panel-hi)", overflow: "auto", padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "var(--t-text-primary)" }}>{detail.label}</div>
              <button onClick={() => setDetail(null)} style={{ background: "none", border: "none", color: "#475569", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>

            <div style={{ ...panel, padding: 12, marginBottom: 12 }}>
              <Row label="Key" value={`mdta_${detail.key_prefix}…`} mono />
              <Row label="Created" value={fmtD(detail.created_at)} />
              <Row label="Last used" value={fmtDT(detail.last_used_at)} />
              <Row label="Calls" value={String(detail.call_count || 0)} />
              <Row label="Expires" value={detail.expires_at ? fmtD(detail.expires_at) : "never"} />
              <Row label="Confirm step" value={detail.require_confirm ? "required" : "off"} />
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
              What this key can do
            </div>
            <div style={{ ...panel, padding: 12, marginBottom: 14 }}>
              {(detail.scopes || []).map(s => {
                const si = SCOPE_INFO[s] || { label: s, color: "#64748b", blurb: "" };
                return (
                  <div key={s} style={{ marginBottom: 9 }}>
                    <span style={{ background: si.color + "22", color: si.color, border: `1px solid ${si.color}44`, borderRadius: 99, padding: "1px 9px", fontSize: 10.5, fontWeight: 700 }}>
                      {si.label}
                    </span>
                    <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 4, lineHeight: 1.55 }}>{si.blurb}</div>
                  </div>
                );
              })}
            </div>

            <button onClick={() => revoke(detail.id)}
              style={{ width: "100%", background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.35)", color: "#f87171", borderRadius: 7, padding: "9px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              Revoke this key
            </button>
          </div>
        )}
      </div>

      {editing && (
        <CreateModal
          scopes={scopes}
          draft={editing}
          onClose={() => setEditing(null)}
          onCreated={(k) => { setEditing(null); setCreated(k); load(); }}
        />
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "4px 0", fontSize: 12 }}>
      <span style={{ color: "#64748b" }}>{label}</span>
      <span style={{ color: "var(--t-text-body)", fontWeight: 600, fontFamily: mono ? "monospace" : "inherit", textAlign: "right" }}>{value}</span>
    </div>
  );
}

function CreateModal({ scopes, draft, onClose, onCreated }) {
  const [label, setLabel]     = useState(draft.label || "");
  const [picked, setPicked]   = useState(new Set(draft.scopes || ["read"]));
  const [expires, setExpires] = useState(draft.expires_days ?? 365);
  const [confirmStep, setConfirmStep] = useState(draft.require_confirm !== false);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);

  const hasOutbound = [...picked].some(s => OUTBOUND.has(s));

  const toggle = (s) => setPicked(p => {
    const n = new Set(p);
    // `read` is implied by everything else; the server re-adds it anyway.
    if (s === "read") return n;
    n.has(s) ? n.delete(s) : n.add(s);
    return n;
  });

  const submit = async () => {
    setErr(null);
    if (!label.trim()) { setErr("Give the key a label so you know what it is for."); return; }
    setBusy(true);
    try {
      const body = {
        label: label.trim(),
        scopes: [...picked],
        expires_days: expires === "never" ? null : Number(expires),
        require_confirm: confirmStep,
      };
      // Turning the confirm step off on a key that can reach a customer has to
      // be said explicitly — the server rejects it otherwise.
      if (!confirmStep && hasOutbound) body.i_understand = true;
      const r = await apiFetch("/assistant/keys", { method: "POST", body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || "Could not create the key."); return; }
      onCreated(d);
    } catch (e) { setErr(e.message || "Could not create the key."); }
    finally { setBusy(false); }
  };

  const panel = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 10 };
  const field = { width: "100%", background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "8px 11px", color: "var(--t-text-body)", fontSize: 13, fontFamily: "inherit" };

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(2,6,23,0.66)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ ...panel, background: "var(--t-panel-hi)", width: 480, maxWidth: "100%", maxHeight: "88vh", overflow: "auto", padding: 20 }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: "var(--t-text-primary)", marginBottom: 14 }}>Create a key</div>

        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>
          Label
        </label>
        <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Chrome extension" style={{ ...field, marginBottom: 14 }} />

        <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>
          What it may do
        </div>
        <div style={{ ...panel, padding: 11, marginBottom: 14 }}>
          {scopes.map(s => {
            const si = SCOPE_INFO[s] || { label: s, color: "#64748b", blurb: "" };
            const on = s === "read" || picked.has(s);
            return (
              <label key={s} style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "6px 0", cursor: s === "read" ? "default" : "pointer" }}>
                <input type="checkbox" checked={on} disabled={s === "read"} onChange={() => toggle(s)}
                  style={{ marginTop: 2, width: 15, height: 15, accentColor: si.color, flexShrink: 0 }} />
                <span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: si.color }}>{si.label}</span>
                  {s === "read" && <span style={{ fontSize: 10.5, color: "#64748b", marginLeft: 6 }}>always on</span>}
                  {si.outbound && <span style={{ fontSize: 10.5, color: "#f87171", marginLeft: 6 }}>reaches customers</span>}
                  <span style={{ display: "block", fontSize: 11.5, color: "#94a3b8", marginTop: 2, lineHeight: 1.5 }}>{si.blurb}</span>
                </span>
              </label>
            );
          })}
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>
          Expires
        </div>
        <select value={expires} onChange={e => setExpires(e.target.value)} style={{ ...field, marginBottom: 14 }}>
          <option value={90}>in 90 days</option>
          <option value={180}>in 180 days</option>
          <option value={365}>in a year</option>
          <option value="never">never</option>
        </select>

        {hasOutbound && (
          <label style={{ display: "flex", gap: 9, alignItems: "flex-start", marginBottom: 14, cursor: "pointer" }}>
            <input type="checkbox" checked={confirmStep} onChange={e => setConfirmStep(e.target.checked)}
              style={{ marginTop: 2, width: 15, height: 15, accentColor: "#f59e0b", flexShrink: 0 }} />
            <span>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--t-text-body)" }}>Ask me to confirm before anything reaches a customer</span>
              <span style={{ display: "block", fontSize: 11.5, color: confirmStep ? "#94a3b8" : "#f87171", marginTop: 2, lineHeight: 1.5 }}>
                {confirmStep
                  ? "Recommended. Replies and emails are held until you approve them."
                  : "Off — this key can email customers with no confirmation step."}
              </span>
            </span>
          </label>
        )}

        {err && (
          <div style={{ background: "rgba(239,68,68,0.09)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 7, padding: "8px 11px", fontSize: 12, color: "#f87171", marginBottom: 12 }}>
            {err}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose}
            style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", color: "#94a3b8", borderRadius: 7, padding: "8px 16px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
            style={{ background: "linear-gradient(135deg,#4f6fff,#7b61ff)", color: "#fff", border: "none", borderRadius: 7, padding: "8px 18px", fontSize: 12.5, fontWeight: 700, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, fontFamily: "inherit" }}>
            {busy ? "Creating…" : "Create key"}
          </button>
        </div>
      </div>
    </div>
  );
}
